// pages/variable/VariableList.jsx
import React, { useMemo, useState } from "react";
import {
  useGetAllConfQuery,
  useCreateConfMutation,
  useUpdateConfMutation,
  useDeleteConfMutation,
} from "../../services/modules/conf/index"; 

const VariableList = () => {
  // Query lista configurazioni
  const { data: confResp, isLoading, isError, refetch } = useGetAllConfQuery();

  // 🔎 estraggo l'array vero dal wrapper (screenshot: resp.data.result)
  const variables = useMemo(() => confResp?.data?.result ?? [], [confResp]);

  // Mutation hook
  const [createConf, { isLoading: isCreating }] = useCreateConfMutation();
  const [updateConf, { isLoading: isUpdating }] = useUpdateConfMutation();
  const [deleteConf, { isLoading: isDeleting }] = useDeleteConfMutation();

  // UI state per modali
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [current, setCurrent] = useState({ id: null, variable: "", value: "" });

  // Handlers
  const handleDelete = async (id) => {
    try {
      await deleteConf(id).unwrap();
      // la lista si aggiorna via invalidatesTags
    } catch (e) {
      console.error("Delete failed:", e);
      alert("Errore durante l'eliminazione");
    }
  };

  const handleSaveEdit = async () => {
    try {
      await updateConf({
        id: current.id,
        variable: current.variable,
        value: current.value,
      }).unwrap();
      setShowEditModal(false);
      setCurrent({ id: null, variable: "", value: "" });
    } catch (e) {
      console.error("Update failed:", e);
      alert("Errore durante l'aggiornamento");
    }
  };

  const handleSaveNew = async () => {
    try {
      await createConf({
        variable: current.variable,
        value: current.value,
      }).unwrap();
      setShowAddModal(false);
      setCurrent({ id: null, variable: "", value: "" });
    } catch (e) {
      console.error("Create failed:", e);
      alert("Errore durante la creazione");
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <h2 className="text-sm font-bold mb-4">Configurazione</h2>
        <div className="bg-white p-6 rounded shadow">Caricamento…</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <h2 className="text-sm font-bold mb-4">Configurazione</h2>
        <div className="bg-white p-6 rounded shadow">
          <div className="mb-4 text-red-600">Errore nel caricamento.</div>
          <button
            onClick={() => refetch()}
            className="bg-black text-white px-4 py-2 rounded"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-4">Configurazione</h2>

      <div className="bg-white p-6 rounded shadow">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-semibold">
            Inserisci le variabili in coppia chiave-valore, ad esempio:
            _NOMEVARIABILE, valore. Es: _LOGO, https://example.com/logo.png
          </h3>
          <button
            className="bg-black text-white px-4 py-2 rounded hover:bg-blue-700"
            onClick={() => {
              setCurrent({ id: null, variable: "", value: "" });
              setShowAddModal(true);
            }}
            disabled={isCreating}
          >
            + Aggiungi
          </button>
        </div>

        <table className="min-w-full border text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-4 py-2 w-5/12">Variabile</th>
              <th className="border px-4 py-2 w-5/12">Valore</th>
              <th className="border px-4 py-2 w-2/12 text-center">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {variables.map((v) => (
              <tr key={v.id}>
                <td className="border px-4 py-2">{v.variable}</td>
                <td className="border px-4 py-2">{v.value}</td>
                <td className="border px-4 py-2">
                  <div className="flex gap-2 justify-center">
                    <button
                      onClick={() => {
                        setCurrent(v);
                        setShowEditModal(true);
                      }}
                      className="bg-gray-200 px-3 py-1 rounded hover:bg-gray-300"
                    >
                      Modifica
                    </button>
                    <button
                      onClick={() => handleDelete(v.id)}
                      className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                      disabled={isDeleting}
                    >
                      Cancella
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {variables.length === 0 && (
              <tr>
                <td colSpan={3} className="text-center p-4 text-stone-500">
                  Nessuna variabile presente.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* MODALE EDIT */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-md w-96">
            <h3 className="text-lg font-semibold mb-4">Modifica Variabile</h3>
            <input
              type="text"
              value={current.variable}
              onChange={(e) =>
                setCurrent((prev) => ({ ...prev, variable: e.target.value }))
              }
              placeholder="Variabile (es. _LOGO)"
              className="border p-2 w-full rounded mb-3"
            />
            <input
              type="text"
              value={current.value}
              onChange={(e) =>
                setCurrent((prev) => ({ ...prev, value: e.target.value }))
              }
              placeholder="Valore"
              className="border p-2 w-full rounded mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowEditModal(false)}
                className="bg-gray-300 px-4 py-2 rounded"
              >
                Annulla
              </button>
              <button
                onClick={handleSaveEdit}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                disabled={isUpdating}
              >
                Salva
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODALE ADD */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-md w-96">
            <h3 className="text-lg font-semibold mb-4">Nuova Variabile</h3>
            <input
              type="text"
              value={current.variable}
              onChange={(e) =>
                setCurrent((prev) => ({ ...prev, variable: e.target.value }))
              }
              placeholder="Variabile (es. _LOGO)"
              className="border p-2 w-full rounded mb-3"
            />
            <input
              type="text"
              value={current.value}
              onChange={(e) =>
                setCurrent((prev) => ({ ...prev, value: e.target.value }))
              }
              placeholder="Valore"
              className="border p-2 w-full rounded mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowAddModal(false)}
                className="bg-gray-300 px-4 py-2 rounded"
              >
                Annulla
              </button>
              <button
                onClick={handleSaveNew}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                disabled={isCreating}
              >
                Salva
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VariableList;
